import { Arrow, Figure, Gate, INK_FAINT, INK_MUTED, Lane, Node, Note } from './primitives';

/**
 * One arrival, from the producer that hands it over to the pages it changes.
 *
 * Laid out as full-width bands down a single spine rather than as two columns. The pipeline has nine
 * stages and only one of them branches, so a column layout spends half the canvas on a gutter and
 * then needs a long wire across it to say "and then the run happens" — which is exactly the join a
 * reader should not have to make, since the two phases *are* the run.
 *
 * Annotations hang off the spine left and right, which is what the width is for: each band states
 * what the stage is in the middle and why it is that way on either side.
 */
export function IngestPath() {
  return (
    <Figure
      title="The global ingestion path, from producer to committed page"
      width={760}
      height={654}
      caption="Nothing in phase one writes to a tree. That is the point of the split: an over-eager placement costs a read, an over-eager page costs a review."
    >
      {/* ── Producers: two groups, by whether anyone is waiting ────── */}
      <Lane x={0} y={20} w={366} h={54} label="immediate — someone is watching" tone="accent" />
      <Node x={12} y={42} w={108} h={22} title="paste · upload" tone="accent" />
      <Node x={128} y={42} w={108} h={22} title="kb_save_note" tone="accent" mono />
      <Node x={244} y={42} w={108} h={22} title="kb_ingest" tone="accent" mono />

      <Lane x={394} y={20} w={366} h={54} label="batched — nobody is waiting" />
      <Node x={406} y={42} w={166} h={22} title="Slack" tone="muted" />
      <Node x={586} y={42} w={162} h={22} title="Granola" tone="muted" />

      <Arrow points={[[183, 74], [183, 88], [310, 88], [310, 104]]} />
      <Arrow points={[[577, 74], [577, 88], [450, 88], [450, 104]]} />

      {/* ── The one door ──────────────────────────────────────────── */}
      <Node x={190} y={104} w={380} h={46} title="receive()" sub="dedup on contentHash, per scope" tone="default" mono />
      <Note x={182} y={122} anchor="end" lines={['the only way in:', 'six producers, none', 'queues content itself']} />
      <Note x={578} y={128} lines={['project named → waits there', 'nothing named → the org root']} />

      <Arrow points={[[380, 150], [380, 176]]} />

      <Node x={190} y={176} w={380} h={44} title="FeedItem + IngestSegment" sub="WAITING · triage true | false" tone="default" />
      <Note x={182} y={194} anchor="end" lines={['hold: writes the item,', 'no segment, no window']} />
      <Note x={578} y={194} lines={['one segment per arrival —', 'nothing splits on the way in']} />

      <Arrow points={[[380, 220], [380, 246]]} />

      {/* ── Waiting ───────────────────────────────────────────────── */}
      <Node x={190} y={246} w={380} h={48} title="batch window" sub="00:00 America/Los_Angeles, by default" tone="info" />
      <Note x={182} y={262} anchor="end" lines={['a once-a-minute tick', 'reads the table']} />
      <Note x={578} y={262} lines={['two pools packed separately,', 'never mixed into one run']} />

      <Arrow points={[[380, 294], [380, 318]]} />

      <Node x={230} y={318} w={300} h={40} title="KbRun" sub="QUEUED · kb-ingestion" tone="default" />
      <Note x={578} y={332} lines={['groupByBudget packs', 'whole arrivals, oldest first']} />

      <Arrow points={[[380, 358], [380, 382]]} />

      {/* ── Admission ─────────────────────────────────────────────── */}
      <Gate x={230} y={382} w={300} h={46} title="admission gate" sub="head rule + project lease" />
      <Arrow
        points={[[230, 405], [160, 405], [160, 338], [230, 338]]}
        dashed
        label="defer"
        labelAt={[156, 374]}
        labelAnchor="end"
      />
      <Note x={578} y={398} lines={['re-enqueued with a jittered', 'backoff; queuedAt untouched']} />

      <Arrow points={[[380, 428], [380, 442], [130, 442], [130, 480]]} label="admitted" labelAt={[386, 439]} labelAnchor="start" />

      {/* ── The run: two phases, one row each ─────────────────────── */}
      <Lane x={0} y={452} w={760} h={84} label="phase one · read-only — nothing has touched a tree" tone="accent" />
      <Node x={16} y={480} w={228} h={44} title="triage()" sub="9 tools · ends at ingest_confirm" tone="accent" mono />
      <Arrow points={[[244, 502], [266, 502]]} />
      <Node x={266} y={480} w={228} h={44} title="destinations + spans" sub="who gets which characters" tone="default" />
      <Arrow points={[[494, 502], [516, 502]]} />
      <Node x={516} y={480} w={228} h={44} title="reserveDestinations()" sub="every lease, or none" tone="info" mono />

      <Arrow points={[[630, 524], [630, 546], [192, 546], [192, 584]]} />

      <Lane x={0} y={556} w={760} h={84} label="phase two · one destination at a time" tone="accent" />
      <Node x={16} y={584} w={352} h={44} title="write loop" sub="11 tools · stage, then kb_commit" tone="accent" />
      <Arrow points={[[368, 606], [392, 606]]} />
      <Node x={392} y={584} w={352} h={44} title="KbNode · KbRevision · KbChange" sub="· KbCitation, verified verbatim at commit" tone="success" />

      <Note x={760} y={444} anchor="end" lines="one run, two phases" size={9} color={INK_MUTED} />
    </Figure>
  );
}

/**
 * Why the queue can run four jobs at once while a project's tree is still single-writer.
 *
 * A timeline rather than a flow, because the claim is about *when* runs overlap, and the one thing a
 * reader has to take away is that the vertical alignment differs between projects and is forbidden
 * within one.
 */
export function WriteGate() {
  const lane = (i: number) => 56 + i * 34;
  const bar = (x: number, w: number, y: number, tone: 'accent' | 'muted') => ({ x, w, y, tone });
  const bars = [
    { label: 'project 1 · run a', ...bar(150, 200, lane(0), 'accent' as const) },
    { label: 'project 2 · run a', ...bar(150, 200, lane(1), 'accent' as const) },
    { label: 'project 3 · run a', ...bar(150, 200, lane(2), 'accent' as const) },
    { label: 'project 1 · run b', ...bar(360, 200, lane(3), 'muted' as const) },
    { label: 'project 1 · run c', ...bar(570, 180, lane(4), 'muted' as const) },
  ];

  return (
    <Figure
      title="Ingestion runs overlap across projects and never within one"
      width={760}
      height={220}
      minWidth={600}
      caption="Two runs against one project would each read the outline, independently conclude the same node is missing, and create it twice. Two runs against different projects share no nodes, no paths and no tree."
    >
      <Note x={0} y={20} lines="TIME →" size={9} weight={700} color={INK_FAINT} />
      {[150, 360, 570, 750].map((x) => (
        <line key={x} x1={x} y1={30} x2={x} y2={196} stroke="var(--color-border)" strokeWidth={1} strokeDasharray="2 5" />
      ))}

      {bars.map((b) => (
        <g key={b.label}>
          <Note x={142} y={b.y + 15} lines={b.label} anchor="end" size={9.5} color={INK_MUTED} />
          <rect x={b.x} y={b.y} width={b.w} height={22} rx={4} fill={b.tone === 'accent' ? 'var(--color-warning-bg)' : 'var(--color-surface-muted)'} stroke={b.tone === 'accent' ? 'var(--color-warning-ring)' : 'var(--color-border)'} />
        </g>
      ))}

      <Note x={356} y={lane(0) + 15} lines="parallel — different trees" size={9} color="var(--color-warning-text)" />
      <Note x={566} y={lane(3) + 15} lines="serial, in arrival order" size={9} color={INK_MUTED} />
      <Note x={360} y={lane(4) + 15} lines="the head rule orders them, by queuedAt" size={9} color={INK_MUTED} />
    </Figure>
  );
}

/**
 * The placement loop's roster, grouped by what each group is *for*.
 *
 * Drawn as a cycle because that is the behaviour the tools are shaped to encourage: review the plan,
 * find a gap or an overlap, go back and gather more. A left-to-right pipeline would suggest one pass.
 */
export function TriageLoop() {
  return (
    <Figure
      title="The placement agent's nine tools, in three groups plus a terminal"
      width={760}
      height={250}
      caption="The loop stops at ingest_confirm or KB_TRIAGE_MAX_STEPS. It never stages, validates or commits — so its budget is spent on “look at one more project”, not on an edit-check-fix cycle."
    >
      <Lane x={0} y={20} w={216} h={140} label="gather" />
      <Node x={16} y={44} w={184} h={24} title="ingest_projects" tone="muted" mono />
      <Node x={16} y={72} w={184} h={24} title="ingest_outline" tone="muted" mono />
      <Node x={16} y={100} w={184} h={24} title="ingest_search" tone="muted" mono />
      <Node x={16} y={128} w={184} h={24} title="ingest_read" tone="muted" mono />

      <Lane x={264} y={20} w={216} h={84} label="decide" />
      <Node x={280} y={44} w={184} h={24} title="ingest_assign" tone="accent" mono />
      <Node x={280} y={72} w={184} h={24} title="ingest_discard" tone="accent" mono />

      <Lane x={528} y={20} w={216} h={84} label="self-correct" />
      <Node x={544} y={44} w={184} h={24} title="ingest_review_plan" tone="info" mono />
      <Node x={544} y={72} w={184} h={24} title="ingest_undo" tone="info" mono />

      <Arrow points={[[216, 62], [264, 62]]} />
      <Arrow points={[[480, 62], [528, 62]]} />

      {/* The loop back: review finds a gap, gathering resumes. */}
      <Arrow
        points={[[636, 104], [636, 184], [108, 184], [108, 160]]}
        dashed
        label="coverage under 100%, or over it"
        labelAt={[372, 180]}
      />

      <Node x={544} y={152} w={184} h={30} title="ingest_confirm" sub="exactly once" tone="success" mono />
      <Arrow points={[[700, 104], [700, 152]]} />

      <Note
        x={0}
        y={218}
        lines={[
          'Batched: search ≤ 12 queries, read ≤ 6 pages, assign and discard ≤ 40 entries each.',
          'Entries are applied in order, never in parallel — sharedWith is computed against what is already placed.',
        ]}
        size={9.5}
      />
    </Figure>
  );
}
